/**
 * Cota gratuita do Gemini no dia (`uso-ia.gemini_hoje`), sem React e testável.
 *
 * O Google dá 20 pedidos por dia ao projeto; o backend reparte em fatias
 * (rodadas em lote, Assistente, prévias pedidas na hora e reserva) e conta
 * pelo dia do Pacífico. Aqui só se lê o que ele manda: nenhum número é
 * fixado na tela.
 */
import { plural } from "../../utils/dinheiro";

/** Fatias do orçamento diário, na ordem em que a tela mostra. */
export const FATIAS_DO_GEMINI = Object.freeze([
  { chave: "lote", rotulo: "Rodadas em lote", ajuda: "Prévias e perfis feitos em lote ao longo do dia" },
  { chave: "assistente", rotulo: "Assistente", ajuda: "Cada pergunta usa um pedido" },
  { chave: "previa", rotulo: "Prévias pedidas na hora", ajuda: "Botão Atualizar prévia da conversa" },
  { chave: "reserva", rotulo: "Reserva", ajuda: "Perfil do cliente escrito na hora e o que sobrar" },
]);

/** A partir desta fração usada, o total do dia fica em atenção. */
const FRACAO_EM_ATENCAO = 0.8;

function numero(valor) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

function data(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "04:00" (hora local). */
export function horaDoGemini(iso) {
  const d = data(iso);
  return d ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
}

/** Usados e limite de uma fatia (ou do dia, sem `fatia`); null se a API não mandou. */
function contagem(gemini, fatia) {
  const fonte = fatia ? gemini?.por_fatia?.[fatia] : gemini;
  const usados = numero(fonte?.usados);
  const limite = numero(fonte?.limite);
  if (usados === null || limite === null) return null;
  return { usados, limite };
}

function sobra(valores) {
  return Math.max(0, valores.limite - valores.usados);
}

/**
 * Pedidos que ainda sobram hoje na fatia (ou no dia); null quando não se sabe.
 * A fatia nunca promete mais do que sobra no dia: o servidor recusa pelo
 * total antes de olhar a fatia (`cota.garantir`), e as 429 da manhã contam no
 * total (29/10: Assistente 7 de 8 com o dia em 22 de 20 dizia "resta 1").
 */
export function restantesNoGemini(gemini, fatia) {
  const valores = contagem(gemini, fatia);
  if (!valores) return null;
  if (!fatia) return sobra(valores);
  const doDia = contagem(gemini);
  return doDia ? Math.min(sobra(valores), sobra(doDia)) : sobra(valores);
}

/** A cota já virou (passou da hora de voltar): o número guardado não vale mais. */
function cotaJaVoltou(gemini, agora) {
  const volta = data(gemini?.volta_em);
  return Boolean(volta) && agora >= volta;
}

/** Pausa pedida pelo Google (limite por minuto ou do dia) ainda valendo; ISO ou null. */
export function pausaDoGemini(gemini, agora = new Date()) {
  const ate = data(gemini?.pausado_ate);
  return ate && ate > agora ? gemini.pausado_ate : null;
}

/**
 * Aviso ao lado de um botão que gasta 1 pedido da fatia:
 * → { texto, esgotado } ("Usa 1 dos pedidos de hoje ao Gemini (restam 3).")
 * Esgotado só enquanto a cota não voltou; sem o número, o aviso sai sem ele.
 */
export function avisoDoPedido(gemini, fatia, agora = new Date()) {
  const restam = cotaJaVoltou(gemini, agora) ? null : restantesNoGemini(gemini, fatia);
  if (restam === 0) {
    const volta = horaDoGemini(gemini?.volta_em);
    return {
      texto: volta ? `Os pedidos de hoje ao Gemini para isto acabaram. Voltam às ${volta}.` : "Os pedidos de hoje ao Gemini para isto acabaram.",
      esgotado: true,
    };
  }
  if (restam === null) return { texto: "Usa 1 dos pedidos de hoje ao Gemini.", esgotado: false };
  return { texto: `Usa 1 dos pedidos de hoje ao Gemini (restam ${restam}).`, esgotado: false };
}

/** Nome do provedor de IA da empresa (ADR-0094) para os textos da cota. */
export const NOME_DO_PROVEDOR = { gemini: "Gemini", openai: "ChatGPT", claude: "Claude" };

export function nomeDoProvedor(gemini) {
  return NOME_DO_PROVEDOR[gemini?.provedor] || "Gemini";
}

/** "Gemini hoje: restam 5 perguntas" para o Assistente; null sem o número. */
export function perguntasRestantes(gemini, agora = new Date()) {
  if (cotaJaVoltou(gemini, agora)) return null;
  const restam = restantesNoGemini(gemini, "assistente");
  if (restam === null) return null;
  if (restam === 0) {
    const volta = horaDoGemini(gemini?.volta_em);
    const nome = nomeDoProvedor(gemini);
    return { restam, texto: volta ? `${nome} hoje: as perguntas acabaram. Voltam às ${volta}.` : `${nome} hoje: as perguntas acabaram.` };
  }
  return { restam, texto: `${nomeDoProvedor(gemini)} hoje: ${restam === 1 ? "resta" : "restam"} ${plural(restam, "pergunta", "perguntas")}` };
}

function tomDoUso(usados, limite) {
  if (limite <= 0 || usados >= limite) return "danger";
  return usados / limite >= FRACAO_EM_ATENCAO ? "warning" : "neutral";
}

/**
 * Painel "Gemini hoje" da Configuração: o total, quando volta, a pausa e cada
 * fatia. Cor só no estado: esgotado (danger) e perto do fim (warning).
 * → null quando a API ainda não manda `gemini_hoje`.
 */
export function resumoDoGeminiHoje(gemini, agora = new Date()) {
  const total = contagem(gemini);
  if (!total) return null;
  const fatias = FATIAS_DO_GEMINI.map((fatia) => {
    const valores = contagem(gemini, fatia.chave);
    if (!valores) return null;
    const esgotada = valores.usados >= valores.limite;
    return { ...fatia, ...valores, esgotada, tom: esgotada ? "danger" : "neutral" };
  }).filter(Boolean);
  return {
    ...total,
    tom: tomDoUso(total.usados, total.limite),
    voltaAs: horaDoGemini(gemini.volta_em),
    pausadoAte: horaDoGemini(pausaDoGemini(gemini, agora)),
    fatias,
  };
}
